import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Plus, Upload } from 'lucide-react';

const GeneralNotesEditor = ({ project, onChange }) => {
  const [customNote, setCustomNote] = useState('');
  
  const generalNotes = project.general_notes || '';
  const landlordNotes = project.landlord_notes || '';
  const landlordName = project.landlord_name || '';
  
  const commonNotes = [
    {
      category: 'Contractor Responsibilities',
      notes: [
        'Contractor shall verify all dimensions in field before ordering materials or commencing work',
        'Contractor shall coordinate all work with other trades',
        'Any discrepancies shall be brought to the attention of the Architect',
        'Contractor shall protect all existing work and finishes',
      ]
    },
    {
      category: 'Architectural Requirements',
      notes: [
        'All work shall conform to applicable building codes',
        'Materials shall be new and of specified quality',
        'Installation shall follow manufacturer recommendations',
        'Provide warranty documentation upon completion',
      ]
    },
    {
      category: 'Fire Department Requirements',
      notes: [
        'Maintain required clearances to fire protection equipment',
        'Do not block access to fire extinguishers or hose cabinets',
        'Emergency exits shall remain unobstructed at all times',
        'Fire alarm devices shall not be covered during construction',
      ]
    },
  ];
  
  const handleAddCommonNote = (note) => {
    const currentNotes = generalNotes ? `${generalNotes}\n\n${note}` : note;
    onChange({ ...project, general_notes: currentNotes });
  };
  
  const handleAddCustomNote = () => {
    if (!customNote.trim()) return;
    
    const currentNotes = generalNotes ? `${generalNotes}\n\n${customNote}` : customNote;
    onChange({ ...project, general_notes: currentNotes });
    setCustomNote('');
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Read file content
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      const currentNotes = generalNotes ? `${generalNotes}\n\n${content}` : content;
      onChange({ ...project, general_notes: currentNotes });
    };
    reader.readAsText(file);
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          General Notes
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          Manage general notes, requirements, and landlord-specific notes
        </p>
      </div>
      
      {/* Common Notes Library */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Common Notes Library
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Click to add standard notes to your project
        </p>
        
        <div className="space-y-4">
          {commonNotes.map((category, catIndex) => (
            <div key={catIndex}>
              <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                {category.category}
              </h4>
              <div className="space-y-2">
                {category.notes.map((note, noteIndex) => (
                  <div key={noteIndex} className="flex items-start space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAddCommonNote(note)}
                      className="h-auto text-left justify-start p-2 flex-1"
                    >
                      <Plus className="mr-2 h-3 w-3 flex-shrink-0" />
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {note}
                      </span>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
      
      {/* General Notes Editor */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            General Notes Content
          </h3>
          
          <div className="flex items-center space-x-2">
            <Label
              htmlFor="file-upload"
              className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Text File
            </Label>
            <input
              id="file-upload"
              type="file"
              accept=".txt"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </div>
        
        <Textarea
          value={generalNotes}
          onChange={(e) => onChange({ ...project, general_notes: e.target.value })}
          placeholder="Enter general notes here... You can type directly or use the library above to add common notes."
          rows={15}
          className="font-mono text-sm"
        />
        
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <p className="text-xs text-slate-600 dark:text-slate-400">
            <strong>Tip:</strong> You can upload a .txt file with pre-written notes, add notes from the library above, or type directly into this editor.
          </p>
        </div>
      </Card>
      
      {/* Add Custom Note */}
      <Card className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/10 dark:to-purple-900/10">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Add Custom Note
        </h3>
        
        <div className="space-y-3">
          <Textarea
            value={customNote}
            onChange={(e) => setCustomNote(e.target.value)}
            placeholder="Write a custom note to add to general notes..."
            rows={3}
          />
          
          <Button
            onClick={handleAddCustomNote}
            disabled={!customNote.trim()}
            className="bg-gradient-to-r from-indigo-600 to-purple-600"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Custom Note
          </Button>
        </div>
      </Card>
      
      {/* Landlord Notes */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Landlord / Property Owner Notes
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Special requirements or notes from the property owner or landlord
        </p>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="landlord_name">Landlord / Property Owner Name</Label>
            <Input
              id="landlord_name"
              value={landlordName}
              onChange={(e) => onChange({ ...project, landlord_name: e.target.value })}
              placeholder="e.g., Hollywood Burbank Airport Authority"
            />
          </div>
          
          <div>
            <Label htmlFor="landlord_notes">Landlord Notes</Label>
            <Textarea
              id="landlord_notes"
              value={landlordNotes}
              onChange={(e) => onChange({ ...project, landlord_notes: e.target.value })}
              placeholder="Enter any specific requirements, regulations, or notes from the landlord..."
              rows={8}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GeneralNotesEditor;